<template>
  <div class="chat-window">
    <MessageList
      :messages="messages"
      :canSelectWinner="props.canSelectWinner"
      :selectedWinnerId="props.selectedWinnerId"
      @selectWinner="
        (messageId, content) => emit('selectWinner', messageId, content)
      "
    />
    <MessageInput
      @send-message="handleSendMessage"
      :disabled="props.isLoading"
    />
  </div>
</template>

<script setup lang="ts">
import { useAuthStore } from "@/stores";
import { AssessmentState } from "@/stores/types";
import { computed, onMounted, ref, watch } from "vue";
import type { Message } from "../types/chat";
import MessageInput from "./MessageInput.vue";
import MessageList from "./MessageList.vue";
import { apiUrl } from "@/config/api";

interface ApiMessageResponse {
  id: string;
  sender: "user" | "ai";
  content: string;
  timestamp: string | Date;
  originalMarkdown?: string;
  structuredResponse?: {
    review: string;
    comment: string;
  };
}

interface ImageObject {
  url: string;
  title: string;
  fileType: string;
}

const props = defineProps<{
  assessmentId: string;
  teacherNotes?: string;
  assignmentContext?: {
    title?: string;
    description?: string;
    criteria?: string[];
  };
  isInitialLoading?: boolean;
  initialMessage: Message | null;
  systemPrompt?: string;
  selectedModel?: string;
  isLoading?: boolean;
  teacherExamples?: string;
  studentImages?: ImageObject[];
  includeImages?: boolean;
  canSelectWinner?: boolean;
  selectedWinnerId?: string | null;
}>();

const emit = defineEmits<{
  loadingChanged: [loading: boolean];
  selectWinner: [messageId: string, content: string];
}>();

const messages = ref<Message[]>([]);
const assessmentState = ref<AssessmentState | null>(null);

const authStore = useAuthStore();

const chatMessages = computed((): Message[] => {
  return assessmentState.value?.chatMessages || [];
});

// Fetch assessment state from API
const loadAssessmentState = async () => {
  if (!props.assessmentId) {
    assessmentState.value = null;
    return;
  }

  try {
    const state = await authStore.getAssessmentState(props.assessmentId);
    assessmentState.value = state;
  } catch (error) {
    console.error("Failed to load assessment state:", error);
    assessmentState.value = null;
  }
};

// Utility function to calculate text statistics
const calculateTextStats = (content: string) => {
  // If content has HTML tags, extract plain text
  let plainText: string;
  if (content.includes("<") && content.includes(">")) {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = content;
    plainText = tempDiv.textContent || tempDiv.innerText || "";
  } else {
    // If it's markdown or plain text, use as-is for word counting
    // Remove markdown formatting for more accurate word count
    plainText = content
      .replace(/[#*_`~[\]()]/g, "") // Remove markdown syntax
      .replace(/![.*?]\(.*?\)/g, "") // Remove image syntax
      .replace(/[.*?]\(.*?\)/g, "") // Remove link syntax
      .trim();
  }

  // Calculate word count (split by whitespace and filter non-empty)
  const words = plainText
    .trim()
    .split(/\s+/)
    .filter((word) => word.length > 0);
  const wordCount = words.length;

  // Calculate character counts
  const characterCount = plainText.length;
  const characterCountNoSpaces = plainText.replace(/\s/g, "").length;

  return {
    wordCount,
    characterCount,
    characterCountNoSpaces,
  };
};

// Watch for assessmentId changes and reload assessment state
watch(
  () => props.assessmentId,
  async (newAssessmentId) => {
    if (newAssessmentId) {
      await loadAssessmentState();
      restoreChatHistory();
    }
  },
);

// Save messages to assessment state via API whenever they change
watch(
  messages,
  async (newMessages) => {
    if (props.assessmentId && newMessages.length > 0) {
      await authStore.updateAssessmentState(props.assessmentId, {
        chatMessages: newMessages,
      });
    }
  },
  { deep: true },
);

// Restore messages from assessment state loaded from API
const restoreChatHistory = () => {
  try {
    const savedMessages = chatMessages.value;
    if (savedMessages && Array.isArray(savedMessages)) {
      // Convert timestamp strings back to Date objects
      messages.value = savedMessages.map((msg: Message) => ({
        ...msg,
        timestamp: new Date(msg.timestamp as string | number | Date),
      }));
    }
  } catch (error) {
    console.error("Failed to restore chat history:", error);
    // Clear corrupted data by updating assessment state
    if (props.assessmentId) {
      void authStore.updateAssessmentState(props.assessmentId, {
        chatMessages: [],
      });
    }
  }
};

// Watch for initial loading state
watch(
  () => props.isInitialLoading,
  (isLoading) => {
    console.log(
      "Initial loading changed:",
      isLoading,
      "Messages length:",
      messages.value.length,
    );
    if (isLoading) {
      // Remove any existing loading messages first
      messages.value = messages.value.filter(
        (msg) => msg.id !== "loading" && !msg.id.startsWith("loading-"),
      );

      // Add loading message
      const loadingMessage: Message = {
        id: "loading",
        sender: "ai",
        content: '<div class="loading-spinner">Arviodaan...</div>',
        timestamp: new Date(),
      };
      messages.value.push(loadingMessage);
      console.log("Added loading message, new length:", messages.value.length);
    }
  },
);

// Watch for initial message
watch(
  () => props.initialMessage,
  (newMessage) => {
    if (newMessage && !messages.value.find((msg) => msg.id === newMessage.id)) {
      // Only add if not already in chat history
      // Remove loading message and add the initial review
      const filteredMessages = messages.value.filter(
        (msg) => msg.id !== "loading",
      );
      messages.value = [...filteredMessages, newMessage];
    }
  },
);

const handleSendMessage = async (content: string) => {
  if (content.trim() === "") return;

  const userMessage: Message = {
    id: `user-${Date.now()}`,
    sender: "user",
    content: `<p>${content}</p>`,
    timestamp: new Date(),
  };
  messages.value.push(userMessage);

  // Emit loading state
  emit("loadingChanged", true);

  // Add loading message
  const loadingMessage: Message = {
    id: `loading-${Date.now()}`,
    sender: "ai",
    content: '<div class="loading-spinner">Päivitetään arviota...</div>',
    timestamp: new Date(),
  };
  messages.value.push(loadingMessage);

  try {
    // Use the assessment ID from props
    const assessmentId = props.assessmentId || "1"; // Fallback to "1" if no prop provided

    if (!props.teacherNotes || props.teacherNotes.trim() === "") {
      throw new Error(
        "Opettajan huomiot vaaditaan ennen arviointikeskustelun aloittamista.",
      );
    }

    const response = await fetch(
      apiUrl(`/assessment_responses/${assessmentId}/review`),
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          message: content,
          teacherNotes: props.teacherNotes,
          assignmentContext: props.assignmentContext,
          systemPrompt: props.systemPrompt,
          model: props.selectedModel,
          teacherExamples: props.teacherExamples,
          includeImages: props.includeImages,
        }),
      },
    );

    if (!response.ok) {
      throw new Error("Vastausta tekoälyltä ei saatu.");
    }

    const apiResponse = (await response.json()) as ApiMessageResponse;
    const aiMessage: Message = {
      ...apiResponse,
      timestamp: new Date(apiResponse.timestamp),
      stats: calculateTextStats(apiResponse.content),
      originalMarkdown: apiResponse.originalMarkdown || apiResponse.content, // Use backend originalMarkdown if available
      structuredResponse: apiResponse.structuredResponse, // Include structured response data
    };

    // Remove loading message and add AI response
    messages.value = messages.value.filter(
      (msg) => !msg.id.startsWith("loading-"),
    );
    messages.value.push(aiMessage);
  } catch (error) {
    console.error("Error fetching AI response:", error);
    const errorMessage: Message = {
      id: `error-${Date.now()}`,
      sender: "ai",
      content: "<p>Pahoittelut, en saanut vastausta. Yritä uudelleen.</p>",
      timestamp: new Date(),
    };

    // Remove loading message and add error message
    messages.value = messages.value.filter(
      (msg) => !msg.id.startsWith("loading-"),
    );
    messages.value.push(errorMessage);
  } finally {
    // Always emit loading state as false when done
    emit("loadingChanged", false);
  }
};

onMounted(async () => {
  // Load assessment state from API first
  await loadAssessmentState();

  // Then restore chat history (which now uses the loaded assessment state)
  restoreChatHistory();

  // Check if initial loading is already true when component mounts
  if (props.isInitialLoading) {
    console.log("Component mounted with isInitialLoading true");
    // Remove any existing loading messages first
    messages.value = messages.value.filter(
      (msg) => msg.id !== "loading" && !msg.id.startsWith("loading-"),
    );

    // Add loading message
    const loadingMessage: Message = {
      id: "loading",
      sender: "ai",
      content: '<div class="loading-spinner">Arviodaan...</div>',
      timestamp: new Date(),
    };
    messages.value.push(loadingMessage);
    console.log(
      "Added loading message on mount, new length:",
      messages.value.length,
    );
  }
});
</script>

<style scoped>
.chat-window {
  display: flex;
  flex-direction: column;
  height: 900px; /* Increased from 600px to 1.5x */
  max-height: 85vh; /* Increased from 70vh to accommodate larger height */
  background-color: #fff;
  padding: 1rem;
}

:deep(.loading-spinner) {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  color: #666;
  font-style: italic;
}

:deep(.loading-spinner)::before {
  content: "";
  width: 20px;
  height: 20px;
  border: 2px solid #f3f3f3;
  border-top: 2px solid #007bff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-right: 0.5rem;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
